import { useState, useEffect, useMemo, useCallback } from 'react';
import arretesService, { ARRETES_CONFIG } from '../services/arretesService';

/**
 * Hook pour gérer la cascade Arrêté → Article → Template.
 *
 * @param {Object} [options]
 * @param {Array<string>|Set<string>|null} [options.allowedArreteIds]
 *   Restreint la liste des arrêtés (ex: arrêtés cochés en étape 2).
 *   Les IDs attendus correspondent à `ARRETES_CONFIG[i].id` (ex: "arrete_19_12_2011").
 *   `null`/`undefined` ou vide = pas de filtre.
 */
export function useArretesTemplates(options = {}) {
  const { allowedArreteIds = null } = options;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ articles: [], templates: [], byArreteCode: new Map(), byArticleId: new Map() });

  const [selectedArreteCode, setSelectedArreteCode] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await arretesService.list();
        if (!cancelled) setData(payload);
      } catch (err) {
        console.error('[useArretesTemplates] chargement échoué:', err);
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Restriction : arrêtés cochés en étape 2 du rapport courant
  const allowedSet = useMemo(() => {
    if (!allowedArreteIds) return null;
    const list = Array.isArray(allowedArreteIds) ? allowedArreteIds : Array.from(allowedArreteIds);
    if (list.length === 0) return null;
    return new Set(list);
  }, [allowedArreteIds]);

  const arretesDisponibles = useMemo(() => {
    // On garde l'ordre de ARRETES_CONFIG
    return ARRETES_CONFIG.filter((a) => {
      if (allowedSet && !allowedSet.has(a.id)) return false;
      // On garde aussi si le serveur n'a renvoyé aucun article : l'utilisateur saisira en libre
      return true;
    });
  }, [allowedSet]);

  const articles = useMemo(() => {
    if (!selectedArreteCode) return [];
    return data.byArreteCode.get(selectedArreteCode) || [];
  }, [data, selectedArreteCode]);

  const templates = useMemo(() => {
    if (!selectedArticleId) return [];
    return data.byArticleId.get(selectedArticleId) || [];
  }, [data, selectedArticleId]);

  const handleArreteChange = useCallback((code) => {
    setSelectedArreteCode(code);
    setSelectedArticleId('');
    setSelectedTemplate(null);
  }, []);

  const handleArticleChange = useCallback((articleId) => {
    setSelectedArticleId(articleId);
    setSelectedTemplate(null);
  }, []);

  const handleTemplateSelect = useCallback((template) => {
    setSelectedTemplate(template);
  }, []);

  const resetFilters = useCallback(() => {
    setSelectedArreteCode('');
    setSelectedArticleId('');
    setSelectedTemplate(null);
  }, []);

  return {
    loading,
    error,
    arretesDisponibles,
    articles,
    templates,
    selectedArreteCode,
    selectedArticleId,
    selectedTemplate,
    handleArreteChange,
    handleArticleChange,
    handleTemplateSelect,
    resetFilters,
  };
}

export default useArretesTemplates;
